import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowRight, Check, Shield, Heart, Lock, 
  ChevronDown, Sparkles, ClipboardCheck, BookOpen,
  Users, Download
} from 'lucide-react';
import { API_URL } from '../config';
import { trackViewContent, trackInitiateCheckout } from '../utils/metaPixel';
import PaymentMarks from '../components/PaymentMarks';
import DistilledSources from '../components/DistilledSources';
import '../styles/ParentsHandbook.css';

// Brand assets — real All Secured logos
const BRAND = {
  shield: 'https://customer-assets.emergentagent.com/job_accordion-repair/artifacts/ic051cbk_Variant%201_Shield%20Only_Vector%20copy.png',
  fullLogo: 'https://customer-assets.emergentagent.com/job_accordion-repair/artifacts/06uiebgi_Variant%201_Full%20Logo_Vector%20copy%202.png'
};

// Founder — author of the handbook & founder of All Secured
const FOUNDER = {
  name: 'Nicholas Georgiev',
  role: 'Founder & Author · All Secured',
  credentials: 'BSc, MBA — University of Queensland',
  photo: 'https://customer-assets.emergentagent.com/job_c5f1c84a-a1d8-4d16-bc73-5c68769811a7/artifacts/s2r8t6o6_image.png'
};

// Premium imagery — warm golden-hour Australian family
const IMAGES = {
  // Hero lifestyle photo — provided by client (mum + son, warm golden hour, tablet)
  heroFamily: 'https://customer-assets.emergentagent.com/job_c5f1c84a-a1d8-4d16-bc73-5c68769811a7/artifacts/uzsh0dyz_ChatGPT%20Image%20Apr%2028%2C%202026%2C%2012_57_56%20PM.png',
  // Parent + child connection — for the "Who it's for" section
  parentChild: 'https://images.pexels.com/photos/3933025/pexels-photo-3933025.jpeg?auto=compress&cs=tinysrgb&w=1400'
};

const ParentsHandbookLanding = () => {
  const [loading, setLoading] = useState(false);
  const [visibleSections, setVisibleSections] = useState({});
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [stickyVisible, setStickyVisible] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el));

    // Meta Pixel — fire ViewContent once on landing page mount
    trackViewContent({
      content_name: "The Parents' Digital Safety Handbook",
      content_id: 'parents-handbook',
      value: 9.99,
    });

    // Sticky CTA — appears when hero scrolls out of view
    const heroEl = document.querySelector('.hb-hero-editorial');
    if (heroEl) {
      const stickyObs = new IntersectionObserver(
        ([entry]) => setStickyVisible(!entry.isIntersecting),
        { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
      );
      stickyObs.observe(heroEl);
      return () => { observer.disconnect(); stickyObs.disconnect(); };
    }
    return () => observer.disconnect();
  }, []);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      // Meta Pixel — fire InitiateCheckout before redirecting to Stripe
      trackInitiateCheckout({
        content_name: "The Parents' Digital Safety Handbook",
        content_id: 'parents-handbook',
        value: 9.99,
      });
      const response = await axios.post(`${API_URL}/api/handbook/checkout`, {
        utm_params: Object.fromEntries(new URLSearchParams(window.location.search))
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const scrollToCheckout = () => {
    document.getElementById('get-handbook')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="hb-page">
      {/* ========== STICKY CTA BAR — appears after hero scrolls out ========== */}
      <div
        className={`hb-sticky-cta ${stickyVisible ? 'visible' : ''}`}
        data-testid="sticky-cta"
        aria-hidden={!stickyVisible}
      >
        <div className="hb-container hb-sticky-cta-row">
          <div className="hb-sticky-cta-brand">
            <img src={BRAND.shield} alt="" />
            <span className="hb-sticky-cta-label">
              <span className="hb-sticky-cta-eyebrow">From All Secured</span>
              <span className="hb-sticky-cta-title">The Digital Safety Handbook · for Australian parents</span>
            </span>
          </div>
          <button
            onClick={handleCheckout}
            disabled={loading}
            className="hb-btn-primary hb-sticky-cta-btn"
            data-testid="sticky-cta-btn"
          >
            {loading ? 'Processing…' : <>Get yours — $9.99 <ArrowRight size={16} /></>}
          </button>
        </div>
        <div className="hb-sticky-cta-marks" aria-hidden="true">
          <PaymentMarks variant="dark" align="center" showStripeNote={false} />
        </div>
      </div>

      {/* ========== HERO — Editorial Magazine: full-bleed family photo with warm dark overlay ========== */}
      <section className="hb-hero-editorial" data-testid="hero-editorial">
        <div
          className="hb-hero-editorial-bg"
          style={{ backgroundImage: `url(${IMAGES.heroFamily})` }}
          aria-hidden="true"
        />
        <div className="hb-hero-editorial-overlay" aria-hidden="true" />
        <div className="hb-hero-editorial-warm" aria-hidden="true" />

        <div className="hb-container hb-hero-editorial-grid">
          {/* Brand seal — top */}
          <div className="hb-prepared-seal hb-prepared-seal-on-dark" data-testid="prepared-seal">
            <span className="hb-prepared-seal-icon">
              <img src={BRAND.shield} alt="All Secured" className="hb-prepared-seal-logo" />
            </span>
            <span className="hb-prepared-seal-text">
              <span className="hb-prepared-seal-eyebrow">From</span>
              <span className="hb-prepared-seal-brand">All Secured</span>
            </span>
          </div>

          {/* "AS DISTILLED FROM" wordmark strip */}
          <div className="hb-hero-distilled-strip" data-testid="hero-distilled-strip" aria-label="Distilled from these sources">
            <span className="hb-hero-distilled-label">Distilled from</span>
            <span className="hb-hero-distilled-marks">
              <span>eSafety</span>
              <span className="hb-hero-distilled-dot" aria-hidden="true">·</span>
              <span>AFP</span>
              <span className="hb-hero-distilled-dot" aria-hidden="true">·</span>
              <span>ACCCE</span>
              <span className="hb-hero-distilled-dot" aria-hidden="true">·</span>
              <span>Kids Helpline</span>
              <span className="hb-hero-distilled-dot" aria-hidden="true">·</span>
              <span>Dolly's Dream</span>
              <span className="hb-hero-distilled-dot" aria-hidden="true">·</span>
              <span>Telethon Kids</span>
            </span>
          </div>

          {/* Headline + subhead */}
          <h1 className="hb-hero-editorial-title" data-testid="hero-editorial-title">
            <span className="hb-hero-editorial-title-line">Australia's calmest,<br/>most practical guide</span>
            <span className="hb-hero-editorial-title-accent">to keeping your kids<br/>safe online.</span>
          </h1>

          <p className="hb-hero-editorial-sub">
            Distilled from <strong>eSafety, AFP, ACCCE, Kids Helpline</strong> and four more —
            into one parent-friendly handbook your family will actually use.
          </p>

          {/* Trust chips row */}
          <div className="hb-hero-editorial-chips" data-testid="hero-editorial-chips">
            <span className="hb-hero-editorial-chip"><Check size={12} /> 36-page PDF</span>
            <span className="hb-hero-editorial-chip"><Check size={12} /> Instant download</span>
            <span className="hb-hero-editorial-chip"><Check size={12} /> One-time payment</span>
          </div>

          {/* Gold CTA */}
          <div className="hb-hero-editorial-cta">
            <button
              onClick={handleCheckout}
              disabled={loading}
              className="hb-btn-gold hb-btn-large hb-btn-block"
              data-testid="hero-cta-btn"
            >
              {loading ? 'Starting checkout…' : (
                <>
                  Get the Handbook — $9.99 AUD
                  <ArrowRight size={18} />
                </>
              )}
            </button>
            <p className="hb-hero-editorial-microtrust">
              Secure Stripe checkout · Read it tonight · No subscription
            </p>
          </div>

          {/* Payment marks — light variant for dark hero */}
          <div className="hb-hero-editorial-payment">
            <PaymentMarks variant="light" align="center" showStripeNote={false} />
          </div>

          {/* Edition seal — subtle, bottom-right */}
          <div className="hb-edition-seal hb-edition-seal-editorial" aria-hidden="true">
            <svg viewBox="0 0 100 100" className="hb-edition-seal-ring">
              <defs>
                <path id="hb-seal-circle-2" d="M 50,50 m -36,0 a 36,36 0 1,1 72,0 a 36,36 0 1,1 -72,0" />
              </defs>
              <text>
                <textPath href="#hb-seal-circle-2" startOffset="0">
                  ALL SECURED · EST 2026 · PARENTS' HANDBOOK ·
                </textPath>
              </text>
            </svg>
            <div className="hb-edition-seal-core">
              <span className="hb-edition-seal-eyebrow">Edition</span>
              <span className="hb-edition-seal-num">I</span>
              <span className="hb-edition-seal-year">2026</span>
            </div>
          </div>
        </div>
      </section>

      {/* ========== DISTILLED FROM THE BEST SOURCES — premium credibility framing ========== */}
      <DistilledSources />

      {/* ========== STAT NUMERALS — editorial pause ========== */}
      <section className="hb-stats" data-testid="stat-strip">
        <div className="hb-container">
          <div className="hb-stats-row">
            <div className="hb-stats-item">
              <span className="hb-stats-num">9</span>
              <span className="hb-stats-label">Chapters</span>
            </div>
            <div className="hb-stats-divider" aria-hidden="true"></div>
            <div className="hb-stats-item">
              <span className="hb-stats-num">36</span>
              <span className="hb-stats-label">Pages</span>
            </div>
            <div className="hb-stats-divider" aria-hidden="true"></div>
            <div className="hb-stats-item">
              <span className="hb-stats-num">12</span>
              <span className="hb-stats-label">Conversation<br/>scripts</span>
            </div>
            <div className="hb-stats-divider" aria-hidden="true"></div>
            <div className="hb-stats-item">
              <span className="hb-stats-num">5</span>
              <span className="hb-stats-label">Family<br/>quizzes</span>
            </div>
          </div>
        </div>
      </section>

      {/* ========== THE MOMENT - Emotional Hook ========== */}
      <section className="hb-section hb-moment" id="moment" data-animate>
        <div className="hb-container hb-container-narrow">
          <p className="hb-moment-text">
            <span className="hb-moment-highlight">You've seen it.</span> Your child, phone in hand, 
            completely absorbed. And somewhere in the back of your mind, a quiet voice asks:
          </p>
          <blockquote className="hb-moment-quote">
            "Do I really know what they're doing online?"
          </blockquote>
          <p className="hb-moment-text">
            It's not about fear. It's about being <strong>prepared</strong>. About having real answers 
            when your child needs guidance. About creating a home where technology is a tool — not a worry.
          </p>
        </div>
      </section>

      {/* ========== WHAT YOU GET - Clear Value ========== */}
      <section className="hb-section hb-value" id="value" data-animate>
        <div className="hb-container">
          <div className="hb-value-content">
            <div className="hb-value-text">
              <span className="hb-eyebrow">What's Inside</span>
              <h2 className="hb-section-title">
                Everything you need.<br/>Nothing you don't.
              </h2>
              <p className="hb-section-subtitle">
                36 pages of clear, calm, actionable guidance — organised so you can find
                exactly what you need, when you need it.
              </p>
              
              <ul className="hb-value-list">
                <li>
                  <div className="hb-value-icon"><Check size={14} strokeWidth={3} /></div>
                  <div>
                    <strong>Conversation scripts for two age groups</strong>
                    <span>Tailored dialogue for ages 5–10 and 10–15 — what to say, when</span>
                  </div>
                </li>
                <li>
                  <div className="hb-value-icon"><Check size={14} strokeWidth={3} /></div>
                  <div>
                    <strong>Family Digital Agreement template</strong>
                    <span>Print, discuss, commit, sign — together</span>
                  </div>
                </li>
                <li>
                  <div className="hb-value-icon"><Check size={14} strokeWidth={3} /></div>
                  <div>
                    <strong>Five built-in quizzes & self-checks</strong>
                    <span>Calm prompts to spot gaps and open up conversations</span>
                  </div>
                </li>
                <li>
                  <div className="hb-value-icon"><Check size={14} strokeWidth={3} /></div>
                  <div>
                    <strong>Real Australian case studies</strong>
                    <span>Murray's Story and Dolly's Dream — what they teach families</span>
                  </div>
                </li>
                <li>
                  <div className="hb-value-icon"><Check size={14} strokeWidth={3} /></div>
                  <div>
                    <strong>Warning-signs summary card & action infographic</strong>
                    <span>What to watch for, and a one-page guide on how to respond</span>
                  </div>
                </li>
              </ul>

              <div className="hb-quiz-callout" data-testid="quiz-callout">
                <span className="hb-quiz-callout-icon">
                  <ClipboardCheck size={20} strokeWidth={2} />
                </span>
                <span className="hb-quiz-callout-text">
                  <span className="hb-quiz-callout-eyebrow">Included inside</span>
                  <span className="hb-quiz-callout-title">Quick family quizzes & self-checks</span>
                  <span className="hb-quiz-callout-desc">
                    Short, calm questions to do with your kids — turn the handbook into a real conversation, not a lecture.
                  </span>
                </span>
              </div>
            </div>
            
            <div className="hb-value-visual">
              <div className="hb-handbook-preview" data-testid="handbook-preview">
                {/* Page 1 — Family Digital Agreement */}
                <article className="hb-preview-page hb-preview-page-1" aria-hidden="true">
                  <div className="hb-preview-pgnum">p. 33</div>
                  <h4 className="hb-preview-title">Family Digital Agreement</h4>
                  <div className="hb-preview-rule" aria-hidden="true" />
                  <p className="hb-preview-body">
                    <strong>Our family agrees:</strong> phones charge in the kitchen
                    overnight. We tell early if something online feels wrong —
                    we'll be helped, not blamed.
                  </p>
                  <div className="hb-preview-sig-row">
                    <div className="hb-preview-sig">
                      <span className="hb-preview-sig-line" />
                      <span className="hb-preview-sig-label">Parent</span>
                    </div>
                    <div className="hb-preview-sig">
                      <span className="hb-preview-sig-line" />
                      <span className="hb-preview-sig-label">Child</span>
                    </div>
                  </div>
                  <div className="hb-preview-date">
                    <span className="hb-preview-date-line" />
                    <span className="hb-preview-date-label">Date</span>
                  </div>
                </article>

                {/* Page 2 — Warning Signs (centre, slight rotation, warm cream) */}
                <article className="hb-preview-page hb-preview-page-2" aria-hidden="true">
                  <div className="hb-preview-pgnum">p. 22</div>
                  <h4 className="hb-preview-title">Warning Signs</h4>
                  <div className="hb-preview-rule" aria-hidden="true" />
                  <ul className="hb-preview-checklist">
                    <li><span className="hb-preview-check-icon" aria-hidden="true" /><span>Sudden secrecy about devices or apps</span></li>
                    <li><span className="hb-preview-check-icon" aria-hidden="true" /><span>Sleep change or withdrawal from family</span></li>
                    <li><span className="hb-preview-check-icon" aria-hidden="true" /><span>New "friends" you've never met</span></li>
                    <li><span className="hb-preview-check-icon" aria-hidden="true" /><span>Strong reaction when you walk in</span></li>
                  </ul>
                </article>

                {/* Page 3 — Conversation Scripts (back-right, leans right) */}
                <article className="hb-preview-page hb-preview-page-3" aria-hidden="true">
                  <div className="hb-preview-pgnum">p. 10</div>
                  <h4 className="hb-preview-title">Conversation Scripts</h4>
                  <div className="hb-preview-rule" aria-hidden="true" />
                  <div className="hb-preview-dialog">
                    <div className="hb-preview-bubble hb-preview-bubble-parent">
                      <span className="hb-preview-bubble-who">Parent</span>
                      <p>"I'm not checking up — I'm checking in. How's your week been online?"</p>
                    </div>
                    <div className="hb-preview-bubble hb-preview-bubble-child">
                      <span className="hb-preview-bubble-who">Child (10-15)</span>
                      <p>"Fine. There's just this one group chat that's been a bit weird."</p>
                    </div>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== CHAPTERS — Editorial Table of Contents ========== */}
      <section className="hb-section hb-chapters" id="chapters" data-animate>
        <div className="hb-container hb-container-narrow">
          <span className="hb-eyebrow">Table of Contents</span>
          <h2 className="hb-section-title hb-center">
            Nine chapters. <span className="hb-section-italic">One clear path.</span>
          </h2>
          <p className="hb-section-subtitle hb-center" style={{ margin: '0 auto 56px' }}>
            Read it cover to cover, or open the chapter you need today —
            it's written so every section can stand alone.
          </p>

          <ol className="hb-toc">
            {[
              { num: '01', title: 'Risks & Benefits of the Digital World',     desc: 'Understand both sides of your child\'s online world',                page: '04' },
              { num: '02', title: 'Staying Connected & Building Confidence',   desc: 'Keep open conversations as they explore',                            page: '10' },
              { num: '03', title: 'Family Rules & Digital Agreement',          desc: 'Boundaries that protect without overwhelming',                       page: '14' },
              { num: '04', title: 'Healthy Digital Habits & Resilience',       desc: 'Balance — sleep, attention, mood, relationships',                    page: '17' },
              { num: '05', title: 'Recognising Warning Signs & Responding',    desc: 'What to watch for, and how to act calmly',                           page: '22' },
              { num: '06', title: 'Murray\'s Story',                            desc: 'A real Australian case study and what it teaches us',               page: '27' },
              { num: '07', title: 'Dolly\'s Dream',                             desc: 'A second case study on resilience and prevention',                  page: '30' },
              { num: '08', title: 'Family Digital Agreement Template',         desc: 'Print, discuss, commit, sign — together',                            page: '33' },
              { num: '09', title: 'Key Tools, Resources & Action Guide',       desc: 'Australian support and a one-page action infographic',              page: '34' },
            ].map((chapter, i) => (
              <li 
                key={i} 
                className={`hb-toc-row ${visibleSections['chapters'] ? 'visible' : ''}`}
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                <span className="hb-toc-num">{chapter.num}</span>
                <span className="hb-toc-title-block">
                  <span className="hb-toc-title">{chapter.title}</span>
                  <span className="hb-toc-desc">{chapter.desc}</span>
                </span>
                <span className="hb-toc-leader" aria-hidden="true"></span>
                <span className="hb-toc-page">p.&nbsp;{chapter.page}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ========== FOUNDER'S NOTE ========== */}
      <section className="hb-section hb-founder" id="founder" data-animate>
        <div className="hb-founder-bg" aria-hidden="true"></div>
        <div className="hb-container hb-founder-grid">
          {/* Portrait column */}
          <aside className="hb-founder-portrait-col">
            <div className="hb-founder-portrait-frame">
              <div className="hb-founder-portrait-glow" aria-hidden="true"></div>
              <img
                src={FOUNDER.photo}
                alt={`${FOUNDER.name}, ${FOUNDER.role}`}
                className="hb-founder-portrait"
              />
              <div className="hb-founder-portrait-seal" aria-hidden="true">
                <img src={BRAND.shield} alt="" />
              </div>
            </div>

            <div className="hb-founder-meta">
              <span className="hb-founder-meta-name">{FOUNDER.name}</span>
              <span className="hb-founder-meta-role">Founder, All Secured</span>
              <span className="hb-founder-meta-rule" aria-hidden="true"></span>
              <span className="hb-founder-meta-credit">Author of this handbook</span>
            </div>
          </aside>

          {/* Note column */}
          <div className="hb-founder-note-col">
            <span className="hb-eyebrow">A note from the founder</span>

            <h2 className="hb-founder-headline">
              I built All Secured to help people feel{' '}
              <span className="hb-founder-italic">safer&nbsp;online.</span>{' '}
              I wrote this handbook so parents could feel{' '}
              <span className="hb-founder-italic">safer&nbsp;at&nbsp;home.</span>
            </h2>

            <div className="hb-founder-quote-mark" aria-hidden="true">"</div>

            <div className="hb-founder-body">
              <p>
                For years, my work in cyber-safety has put me in rooms with executives,
                analysts and policy-makers — talking about firewalls, frameworks, and
                risk registers.
              </p>
              <p>
                But the conversation I kept hearing wasn't in those rooms. It was at the
                school gate. Around the dinner table. From friends, family, and everyday
                parents who weren't tech-savvy and didn't want to be — they just wanted
                to know their kids were okay.
              </p>
              <p className="hb-founder-emphasis">
                I wrote this handbook for them. <strong>For you.</strong>
              </p>
              <p>
                Every chapter, every conversation script, every family quiz inside it is
                shaped by one quiet belief:{' '}
                <span className="hb-founder-pull">
                  parents don't need to become experts to keep their children safe online —
                  they just need someone to walk beside them, calmly, with a clear path.
                </span>
              </p>
              <p>
                That's what All Secured stands for. And that's exactly what this handbook
                is built to be.
              </p>
              <p className="hb-founder-thanks">
                Thank you for trusting it with your family.
              </p>
            </div>

            <div className="hb-founder-sign">
              <span className="hb-founder-sign-name">{FOUNDER.name}</span>
              <span className="hb-founder-sign-role">{FOUNDER.role}</span>
              <span className="hb-founder-sign-creds">{FOUNDER.credentials}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ========== WHO IT'S FOR ========== */}
      <section className="hb-section hb-who" id="who" data-animate>
        <div className="hb-container">
          <div className="hb-who-content">
            <div className="hb-who-image">
              <img src={IMAGES.parentChild} alt="Parent and child together" />
            </div>
            <div className="hb-who-text">
              <span className="hb-eyebrow">Is this for me?</span>
              <h2 className="hb-section-title">
                Made for parents who want to be prepared — not paranoid.
              </h2>
              <div className="hb-who-list">
                <div className="hb-who-item">
                  <Heart size={20} />
                  <span>Parents of children aged 5-15</span>
                </div>
                <div className="hb-who-item">
                  <Heart size={20} />
                  <span>Families wanting clearer digital boundaries</span>
                </div>
                <div className="hb-who-item">
                  <Heart size={20} />
                  <span>Non-technical parents who want practical steps</span>
                </div>
                <div className="hb-who-item">
                  <Heart size={20} />
                  <span>Anyone who'd rather prevent problems than react to them</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== PURCHASE SECTION ========== */}
      <section className="hb-section hb-purchase" id="get-handbook" data-animate>
        <div className="hb-container hb-container-narrow">
          <div className="hb-purchase-card">
            <div className="hb-purchase-badge">
              <Sparkles size={14} />
              <span>Instant Download</span>
            </div>
            
            <h2 className="hb-purchase-title">
              The Digital Safety Handbook
            </h2>
            
            <p className="hb-purchase-desc">
              A practical, calm 36-page guide to raising cyber-safe kids in Australia.
            </p>
            
            <div className="hb-purchase-price">
              <span className="hb-price-amount">$9.99</span>
              <span className="hb-price-currency">AUD</span>
            </div>
            
            <ul className="hb-purchase-includes">
              <li><Check size={16} /> Complete 36-page handbook (PDF)</li>
              <li><Check size={16} /> Age-tailored conversation scripts (5–10 & 10–15)</li>
              <li><Check size={16} /> Family Digital Agreement template</li>
              <li><Check size={16} /> Five family quizzes & self-checks</li>
              <li><Check size={16} /> Two real Australian case studies</li>
              <li><Check size={16} /> Warning-signs summary card & action infographic</li>
            </ul>
            
            <button 
              className="hb-btn-primary hb-btn-large"
              onClick={handleCheckout}
              disabled={loading}
              data-testid="purchase-checkout-btn"
            >
              {loading ? 'Processing...' : (
                <>
                  Get Instant Access
                  <ArrowRight size={20} />
                </>
              )}
            </button>

            <PaymentMarks variant="dark" align="center" showStripeNote={true} />

            <div className="hb-purchase-secure">
              <Lock size={14} />
              <span>Secure Stripe checkout • Instant PDF delivery</span>
            </div>

            <div className="hb-secure-row">
              <span className="hb-secure-pill">
                <Lock size={12} /> Stripe • SSL encrypted
              </span>
              <span className="hb-secure-pill">
                <Check size={12} /> One-time payment
              </span>
              <span className="hb-secure-pill">
                <Sparkles size={12} /> Instant download
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FAQ ========== */}
      <section className="hb-section hb-faq" id="faq" data-animate>
        <div className="hb-container hb-container-narrow">
          <h2 className="hb-section-title hb-center">Common questions</h2>
          
          <div className="hb-faq-list">
            {[
              { q: 'Is this written for non-technical parents?', a: 'Absolutely. Plain language, no jargon. If you can read an email, you can use this handbook.' },
              { q: 'What age groups does it cover?', a: 'The handbook is built around two age bands — 5 to 10, and 10 to 15 — with model parent scripts and quizzes written specifically for each stage.' },
              { q: 'What are the family quizzes?', a: 'Five short, calm self-check sets you can do with your kids — they help you spot gaps in their digital habits and open up better conversations. They\'re built into the handbook, not a separate tool.' },
              { q: 'Is it a physical book?', a: 'It\'s an instant 36-page PDF download. Read it on any device, print the pages you need, or keep it on your phone for quick reference.' },
              { q: 'Is this fear-based content?', a: 'No. The handbook takes a calm, balanced approach — empowering you with knowledge and concrete language, never scaring you into action.' },
              { q: 'Are the sources reliable?', a: 'Yes. Guidance is drawn from publicly available material from the Australian eSafety Commissioner, ACCCE, the Australian Federal Police, Kids Helpline, Dolly\'s Dream, the Telethon Kids Institute and other established child-safety voices.' },
            ].map((faq, i) => (
              <div 
                key={i} 
                className={`hb-faq-item ${expandedFaq === i ? 'expanded' : ''}`}
                onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
              >
                <div className="hb-faq-q">
                  <span>{faq.q}</span>
                  <ChevronDown size={20} />
                </div>
                <div className="hb-faq-a">
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA — Cinematic ========== */}
      <section className="hb-section hb-final" id="final" data-animate>
        <div
          className="hb-final-bg"
          style={{ backgroundImage: `url(${IMAGES.heroFamily})` }}
          aria-hidden="true"
        ></div>
        <div className="hb-final-warm" aria-hidden="true"></div>
        <div className="hb-final-overlay" aria-hidden="true"></div>
        <div className="hb-container hb-container-narrow hb-center hb-final-content">
          <span className="hb-eyebrow hb-final-eyebrow">Begin today</span>
          <h2 className="hb-final-title">
            Give yourself the confidence to{' '}
            <span className="hb-final-italic">guide them.</span>
          </h2>
          <p className="hb-final-subtitle">
            The digital world isn't going anywhere — but with the right
            guidance, you can help your children navigate it safely.
          </p>
          <button 
            className="hb-btn-primary hb-btn-large hb-final-cta"
            onClick={handleCheckout}
            disabled={loading}
            data-testid="final-checkout-btn"
          >
            {loading ? 'Processing...' : (
              <>
                Get the Handbook — $9.99
                <ArrowRight size={20} />
              </>
            )}
          </button>
          <div className="hb-final-trust">
            <Lock size={14} />
            <span>Secure Stripe checkout · Instant PDF delivery</span>
          </div>
          <PaymentMarks variant="light" align="center" showStripeNote={false} />
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="hb-footer">
        <div className="hb-container">
          <div className="hb-footer-content">
            <Link to="/" className="hb-footer-logo">
              <img src={BRAND.fullLogo} alt="All Secured" />
            </Link>
            <p>A safer digital world — until it's second nature.</p>
            <div className="hb-footer-links">
              <Link to="/privacy">Privacy</Link>
              <Link to="/terms">Terms</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ParentsHandbookLanding;
